import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';

import { BaseComponent } from './BaseComponent';
import { BaseModel } from '../../../models/email-template/base-model.interface';
import { ToastBarService } from '../../../services/toast-bar.service';

/** A block model whose `variables` are addressed by dynamic string keys (e.g. `Headline_1`). */
type VariableListBlock = BaseModel & { variables?: { [key: string]: string } };

/**
 * Shared list-management logic for "dynamic variable list" blocks (headline, description): a list
 * of text variables (`<Prefix>_1`, `<Prefix>_2`, …) the user can grow/shrink between a min and a
 * max. Subclasses only declare the prefix and the min/max bounds; component-specific event wiring
 * (onChange, removeBlock, onContainerClick) stays in the concrete components.
 */
@Component({
  selector: 'app-base-variable-list-block',
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export abstract class BaseVariableListBlockComponent extends BaseComponent implements OnInit {
  /** Variable name prefix, e.g. 'Headline' -> keys 'Headline_1'. */
  protected abstract readonly variablePrefix: string;
  /** Fewest variables that must always remain visible. */
  protected abstract readonly minVariables: number;
  /** Most variables that can be added. (Public: read by the templates' counter.) */
  abstract readonly maxVariables: number;

  @Input() block: VariableListBlock = { blockId: '', name: '', count: 0, variables: {} };
  @Input() isPreview = false;
  @Output() blockData = new EventEmitter<any>();
  @Output() removeBlockEvent = new EventEmitter<string | null>();

  /** Keys of the variables currently shown, e.g. ['Headline_1', 'Headline_2', …]. */
  keys: string[] = [];

  constructor(toastBarService: ToastBarService, protected cdr: ChangeDetectorRef) {
    super(toastBarService);
  }

  ngOnInit(): void {
    const variables = this.block.variables;
    // Show every variable that already has a value, but never fewer than the minimum.
    let visibleCount = this.minVariables;
    for (let i = this.maxVariables; i > visibleCount; i--) {
      if (variables?.[`${this.variablePrefix}_${i}`]) {
        visibleCount = i;
        break;
      }
    }
    this.keys = this.buildKeys(visibleCount);
  }

  get canAdd(): boolean {
    return this.keys.length < this.maxVariables;
  }

  add(): void {
    if (this.canAdd) {
      this.keys = this.buildKeys(this.keys.length + 1);
    }
  }

  variableRemoval(variable: string): void {
    if (this.keys.length - 1 < this.minVariables) {
      this.toastBarService.showToastError(
        `Can't delete ${variable} because the template would have less than minimum entries!`
      );
      return;
    }

    const deletedVariableIndex = this.keys.indexOf(variable);
    if (deletedVariableIndex === -1) {
      this.toastBarService.showToastError(`${variable} doesn't exist!`);
      return;
    }

    // Remove the variable from the visible keys.
    this.keys.splice(deletedVariableIndex, 1);
    this.keys = this.buildKeys(this.keys.length);

    // Remove it from the block, then re-number the remaining variables so the keys stay
    // contiguous (Prefix_1, Prefix_2, …).
    const variables = this.block.variables;
    if (variables) {
      delete variables[variable];
    }
    const renamed: { [key: string]: string } = {};
    Object.values(variables ?? {}).forEach((value, i) => {
      renamed[`${this.variablePrefix}_${i + 1}`] = value;
    });
    this.block.variables = renamed;

    // Force the block-text rows to be destroyed and re-created so each one re-reads its (now
    // shifted) value in ngOnInit. Without this, Angular reuses the rows by positional key and
    // each child keeps its old cached text, making it look like the LAST entry was removed
    // instead of the clicked one.
    const remainingKeys = this.keys;
    this.keys = [];
    this.cdr.detectChanges();
    this.keys = remainingKeys;

    this.blockData.emit(this.block);
  }

  protected buildKeys(count: number): string[] {
    return Array.from({ length: count }, (_, i) => `${this.variablePrefix}_${i + 1}`);
  }
}
